import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { useRecoilValue } from "recoil";
import { authState } from "../../recoil/authState";
import API from "../../utils/api";

export default function PasswordChangeDialog({ open, onClose }) {
  const auth = useRecoilValue(authState);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handlePasswordChange = async () => {
    // 유효성 검증
    if (!newPassword || newPassword.length < 3) {
      setPasswordError("비밀번호는 3자 이상이어야 합니다.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("비밀번호가 일치하지 않습니다.");
      return;
    }

    setPasswordError("");
    setPasswordLoading(true);

    try {
      const token = localStorage.getItem("auth-token");
      const userId = auth.user?.id;

      if (!userId) {
        setPasswordError("사용자 정보를 찾을 수 없습니다.");
        setPasswordLoading(false);
        return;
      }

      const apiUrl = `${process.env.REACT_APP_API_URL}${API.USER_PASSWORD_CHANGE}/${userId}/password`;
      console.log("[비밀번호 변경] API 호출:", apiUrl);
      console.log("[비밀번호 변경] 사용자 ID:", userId);
      
      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          newPassword: newPassword,
        }),
      });

      console.log("[비밀번호 변경] 응답 상태:", response.status);
      console.log("[비밀번호 변경] 응답 헤더:", response.headers.get("content-type"));

      // 응답이 JSON인지 확인
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("비밀번호 변경 API 응답 오류 (HTML):", text.substring(0, 200));
        setPasswordError(`서버 오류가 발생했습니다. (상태 코드: ${response.status})`);
        setPasswordLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        alert("비밀번호가 성공적으로 변경되었습니다.");
        handleClose();
      } else {
        setPasswordError(data.message || "비밀번호 변경 중 오류가 발생하였습니다.");
      }
    } catch (error) {
      console.error("비밀번호 변경 오류:", error);
      if (error.message && error.message.includes("JSON")) {
        setPasswordError("서버 응답 오류가 발생했습니다. 관리자에게 문의하세요.");
      } else {
        setPasswordError(error.message || "비밀번호 변경 중 오류가 발생하였습니다.");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>비밀번호 변경</DialogTitle>
      <DialogContent>
        {passwordError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {passwordError}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="새 비밀번호"
          type="password"
          fullWidth
          variant="outlined"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={passwordLoading}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="비밀번호 확인"
          type="password"
          fullWidth
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={passwordLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={passwordLoading}>
          취소
        </Button>
        <Button
          onClick={handlePasswordChange}
          variant="contained"
          disabled={passwordLoading}
        >
          {passwordLoading ? "변경 중..." : "변경"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
